// WbTicketDetailPage — /weighbridge/tickets/:enc_id (Phase C9 Group 2).
//
// Read-only ticket detail page. Renders:
//   - LEFT: Party & Equipment, Weights, Weight Events (collapsible),
//           Line Items + totals, Notes (when present).
//   - RIGHT: Actions card (status-aware), Snapshots (when present),
//           Payment (completed only), Void info (voided only).
//
// The ONLY mutation wired in this phase is `useReissueTicket` — the
// other status-change buttons (void / print / second-weigh) land in
// later phases and are rendered disabled with explanatory tooltips.
//
// Snapshots: the DTO carries storage keys, not URLs. We resolve them via
// inline `useQuery` calls against /files/presigned (mirrors the pattern
// used by WbDriverDetailPage for signature images — see that file lines
// 117-137 for the canonical version).

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Ban, RotateCcw, Send } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Badge, type BadgeVariant } from '../../../components/ui/Badge/Badge';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { extractErrorMessage } from '../../../utils/errorMessage';
import { useToastStore } from '../../../store/toastStore';
import { useAuthStore } from '../../../store/authStore';
import {
  useMarkTicketOpen,
  useReissueTicket,
  useWbTicket,
  type TicketDetailDto,
  type TicketDirection,
  type TicketLineDto,
  type TicketStatus,
  type UnitPriceBasis,
  type WeighingMode,
  type WeightEventDto,
  type WeightMethod,
} from '../hooks/useWbTicket';
import type { TicketLockDto } from '../hooks/useWbTickets';
import { WbSnapshotThumb } from '../components/WbSnapshotThumb';
import { WbVoidTicketModal } from './WbVoidTicketModal';
import { WbPrintButton } from './components/WbPrintButton';
import styles from './WbTicketDetailPage.module.css';

// -- Activity entry ------------------------------------------------------
//
// Mirrors the server `WbTicketActivityDto` (wb_activity, mig 142) returned by
// GET /wb/tickets/:enc_id/activity. The legacy activity_logs shape (action /
// details / staff_id) is gone — wb_activity carries a structured event_type, a
// denormalised actor_name snapshot, and a JSON metadata payload.
interface ActivityLogEntry {
  readonly id: number;
  readonly event_type: string;
  readonly actor_name: string | null;
  readonly entity_ref: string | null;
  readonly metadata: Record<string, unknown>;
  readonly created_at: string;
}

interface ActivityLogResponse {
  readonly activity: ReadonlyArray<ActivityLogEntry>;
  readonly pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Event-type → readable badge. Mirrors the super console's EVENT_META so the
// tenant and super views label events identically. Unknown events fall back to
// the title-cased raw string with a neutral variant.
const EVENT_LABEL: Record<string, { label: string; variant: BadgeVariant }> = {
  'ticket.created': { label: 'Created', variant: 'success' },
  'ticket.patched': { label: 'Updated', variant: 'muted' },
  'ticket.weight_captured': { label: 'Weight captured', variant: 'info' },
  'ticket.snapshot_added': { label: 'Snapshot added', variant: 'muted' },
  'ticket.completed': { label: 'Completed', variant: 'info' },
  'ticket.voided': { label: 'Voided', variant: 'warning' },
  'ticket.deleted': { label: 'Deleted', variant: 'danger' },
  'ticket.reissued': { label: 'Reissued', variant: 'info' },
  'ticket.advice_note_sent': { label: 'Advice note sent', variant: 'muted' },
};

function eventLabel(raw: string): { label: string; variant: BadgeVariant } {
  return (
    EVENT_LABEL[raw] ?? {
      label: raw
        .replace(/^ticket\./, '')
        .split(/[._]/)
        .map((p) => (p.length === 0 ? p : p[0]!.toUpperCase() + p.slice(1)))
        .join(' '),
      variant: 'default',
    }
  );
}

// Best-effort detection of "this line is a waste line" — we don't have
// product.is_hazardous on TicketLineDto, so we use ewc_code_snapshot as
// the proxy (waste lines carry an EWC code; non-waste lines don't).
function hasWasteLine(ticket: TicketDetailDto): boolean {
  return ticket.lines.some(
    (l) => l.ewc_code_snapshot !== null && l.ewc_code_snapshot.length > 0,
  );
}

// Hazardous consignment uses the same proxy as waste lines today — the
// TicketLineDto doesn't expose product.is_hazardous separately. When the
// server starts surfacing is_hazardous per line we can tighten this.
const hasHazardousLine = hasWasteLine;

// -- Formatters -------------------------------------------------------------

const GBP = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
});

function fmtCurrency(n: number): string {
  return GBP.format(n);
}

function fmtKg(n: number | null): string {
  if (n === null || n === undefined) return '—';
  return `${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(n)} kg`;
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB');
}

function fmtNumber(n: number, max = 3): string {
  return new Intl.NumberFormat('en-GB', { maximumFractionDigits: max }).format(n);
}

// -- Badge / label helpers --------------------------------------------------

const STATUS_LABEL: Record<TicketStatus, string> = {
  draft: 'Draft',
  open: 'Open',
  completed: 'Completed',
  voided: 'Voided',
};

const STATUS_VARIANT: Record<TicketStatus, BadgeVariant> = {
  draft: 'muted',
  open: 'info',
  completed: 'success',
  voided: 'danger',
};

const DIRECTION_LABEL: Record<TicketDirection, string> = {
  inwards: 'Inwards',
  outwards: 'Outwards',
};

const WEIGHING_MODE_LABEL: Record<WeighingMode, string> = {
  two_pass: 'Two-pass',
  stored_tare: 'Stored tare',
  manual_tare: 'Manual tare',
  units_only: 'Units only',
};

const METHOD_LABEL: Record<WeightMethod, string> = {
  hardware: 'Hardware',
  manual: 'Manual',
  stored: 'Stored',
};

const LINE_MODE_LABEL: Record<TicketLineDto['mode'], string> = {
  weight: 'Weight',
  unit: 'Unit',
  custom: 'Custom',
};

function statusBadge(status: TicketStatus): JSX.Element {
  return <Badge variant={STATUS_VARIANT[status]}>{STATUS_LABEL[status]}</Badge>;
}

function directionBadge(direction: TicketDirection): JSX.Element {
  return <Badge variant="default">{DIRECTION_LABEL[direction]}</Badge>;
}

function methodBadge(method: WeightMethod | null): JSX.Element {
  if (method === null) return <span className={styles.muted}>—</span>;
  return <Badge variant="muted">{METHOD_LABEL[method]}</Badge>;
}

function basisBadge(basis: UnitPriceBasis | null): JSX.Element {
  if (basis === null) return <span className={styles.muted}>—</span>;
  // price_band intentionally renders with the `info` (blue) variant —
  // the design system has no purple variant. Swap to purple when it
  // ships rather than introducing a one-off CSS colour for a single
  // badge (flag C8, deferred by design).
  if (basis === 'contract') return <Badge variant="info">Contract</Badge>;
  if (basis === 'price_band') return <Badge variant="info">Band</Badge>;
  if (basis === 'general_public') {
    return <Badge variant="muted">General public</Badge>;
  }
  // manual_override → warning (amber)
  return <Badge variant="warning">Manual override</Badge>;
}

// -- Page -------------------------------------------------------------------

export function WbTicketDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const { enc_id } = useParams<{ enc_id: string }>();
  const ticketQuery = useWbTicket(enc_id);
  const reissueMut = useReissueTicket(enc_id ?? '');
  const markOpenMut = useMarkTicketOpen(enc_id ?? '');
  const pushToast = useToastStore((s) => s.push);
  const currentStaffId = useAuthStore((s) => s.user?.id ?? null);
  const [voidOpen, setVoidOpen] = useState(false);

  // Ticket-locking (migration 167). The detail page is a read-only preview; it
  // only READS the current lock status to drive the always-on "who holds the
  // lock" banner. Acquiring / heartbeating / releasing the lock now lives on
  // the continue-ticket editor (WbNewTicketPage), which is where editing
  // actually happens. "Continue ticket" navigates there.
  const [activeLock, setActiveLock] = useState<TicketLockDto | null>(null);
  const loadedEncId = ticketQuery.data?.ticket?.enc_id;

  // Read current lock status on open (preview mode — no lock acquired).
  useEffect(() => {
    if (loadedEncId === undefined) return;
    void Promise.resolve(
      apiClient.get<TicketLockDto | null>(endpoints.wb.ticketLock(loadedEncId)),
    )
      .then((res) => setActiveLock(res.data))
      .catch(() => {
        /* non-fatal */
      });
  }, [loadedEncId]);

  const sendAdviceNoteMut = useMutation<{ queued: boolean }, unknown, void>({
    mutationFn: async () => {
      const res = await apiClient.post<{ queued: boolean }>(
        endpoints.wb.ticketSendAdviceNote(enc_id ?? ''),
        {},
      );
      return res.data;
    },
    onSuccess: (data) => {
      pushToast({
        type: data.queued ? 'success' : 'info',
        message: data.queued
          ? 'Advice note queued for sending.'
          : 'No email address on file for this customer — nothing was queued.',
      });
    },
    onError: (err) => {
      pushToast({
        type: 'error',
        message:
          extractErrorMessage(err) ?? 'Failed to queue advice note.',
      });
    },
  });

  if (ticketQuery.isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Ticket"
          actions={
            <Button
              variant="secondary"
              icon={ArrowLeft}
              onClick={() => navigate('/weighbridge/tickets')}
            >
              Back to Tickets
            </Button>
          }
        />
        <Card>
          <div className={styles.loading}>
            <Spinner label="Loading ticket" />
          </div>
        </Card>
      </div>
    );
  }

  if (ticketQuery.isError || !ticketQuery.data?.ticket) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Ticket"
          actions={
            <Button
              variant="secondary"
              icon={ArrowLeft}
              onClick={() => navigate('/weighbridge/tickets')}
            >
              Back to Tickets
            </Button>
          }
        />
        <Card>
          <p className={styles.error}>
            {extractErrorMessage(ticketQuery.error) ?? 'Failed to load ticket.'}
          </p>
        </Card>
      </div>
    );
  }

  const t = ticketQuery.data.ticket;
  const title = t.ticket_number ?? 'Draft ticket';
  const lockHolder =
    activeLock !== null && !activeLock.is_expired ? activeLock : null;
  // Another staff member holds a fresh lock → the editor is off-limits, so the
  // "Continue ticket" / "Capture second weigh" actions are disabled (mig 167).
  const lockedByAnother =
    lockHolder !== null && lockHolder.locked_by !== currentStaffId;

  function handleReissue() {
    reissueMut.mutate(undefined, {
      onSuccess: (res) => {
        pushToast({ type: 'success', message: 'Ticket reissued.' });
        navigate(`/weighbridge/tickets/${res.ticket.enc_id}`);
      },
      onError: (err) => {
        pushToast({
          type: 'error',
          message: extractErrorMessage(err) ?? 'Failed to reissue ticket.',
        });
      },
    });
  }

  function handleMarkOpen() {
    markOpenMut.mutate(undefined, {
      onSuccess: () => {
        pushToast({ type: 'success', message: 'Ticket marked as open.' });
      },
      onError: (err) => {
        pushToast({
          type: 'error',
          message: extractErrorMessage(err) ?? 'Failed to mark ticket as open.',
        });
      },
    });
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={title}
        subtitle={
          <span className={styles.subtitle}>
            {statusBadge(t.status)}
            {directionBadge(t.direction)}
            <span>Created {fmtDateTime(t.created_at)}</span>
          </span>
        }
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate('/weighbridge/tickets')}
          >
            Back to Tickets
          </Button>
        }
      />

      {lockHolder !== null ? (
        <div
          className={styles.lockBanner}
          data-own={lockHolder.locked_by === currentStaffId ? 'true' : 'false'}
          data-testid="wb-lock-banner"
        >
          🔒{' '}
          {lockHolder.locked_by === currentStaffId
            ? 'You are currently editing this ticket.'
            : `Locked by ${lockHolder.locked_by_name} (${lockHolder.origin}) — ticket cannot be edited until the lock is released.`}
        </div>
      ) : null}

      <div className={styles.grid}>
        {/* -- LEFT COLUMN ---------------------------------------------- */}
        <div className={styles.column}>
          <PartyAndEquipmentCard ticket={t} />
          <WeightsCard ticket={t} />
          <WeightEventsCard events={t.weight_events} />
          <LineItemsCard ticket={t} />
          {t.notes !== null || t.internal_notes !== null ? (
            <NotesCard ticket={t} />
          ) : null}
          <ActivityCard ticketEncId={t.enc_id} />
        </div>

        {/* -- RIGHT COLUMN --------------------------------------------- */}
        <div className={styles.column}>
          <ActionsCard
            ticket={t}
            lockedByAnother={lockedByAnother}
            lockedByName={lockedByAnother ? lockHolder!.locked_by_name : null}
            onContinue={() =>
              navigate(`/weighbridge/tickets/new?from=${t.enc_id}`)
            }
            onSecondWeigh={() =>
              // C11 — second-weigh resumes the ?from=enc_id flow on the
              // new-ticket page, which renders the tare capture UI for a
              // draft that has a gross but no tare yet.
              navigate(`/weighbridge/tickets/new?from=${t.enc_id}`)
            }
            onReissue={handleReissue}
            isReissuing={reissueMut.isPending}
            onMarkOpen={handleMarkOpen}
            isMarkingOpen={markOpenMut.isPending}
            onOpenVoidModal={() => setVoidOpen(true)}
            onSendAdviceNote={() => sendAdviceNoteMut.mutate()}
            isSendingAdviceNote={sendAdviceNoteMut.isPending}
          />

          {t.snapshots.length > 0 ? (
            <Card title="Snapshots">
              <div className={styles.snapshotGrid}>
                {t.snapshots.map((s) => (
                  <WbSnapshotThumb
                    key={s.id}
                    scopeKey={t.enc_id}
                    snapshot={s}
                  />
                ))}
              </div>
            </Card>
          ) : null}

          {t.status === 'completed' ? <PaymentCard ticket={t} /> : null}
          {t.status === 'voided' ? <VoidInfoCard ticket={t} /> : null}
        </div>
      </div>

      <WbVoidTicketModal
        open={voidOpen}
        ticketEncId={t.enc_id}
        onClose={() => setVoidOpen(false)}
      />
    </div>
  );
}

// -- Card components --------------------------------------------------------

function dashOrText(value: string | null | undefined): JSX.Element {
  if (value === null || value === undefined || value === '') {
    return <span className={styles.muted}>—</span>;
  }
  return <>{value}</>;
}

function PartyAndEquipmentCard({ ticket }: { ticket: TicketDetailDto }): JSX.Element {
  return (
    <Card title="Party & Equipment">
      <dl className={styles.dl}>
        <dt>Customer</dt>
        <dd>{dashOrText(ticket.customer?.name ?? null)}</dd>

        <dt>Haulier</dt>
        <dd>{dashOrText(ticket.haulier?.name ?? null)}</dd>

        <dt>Driver</dt>
        <dd>{dashOrText(ticket.driver?.name ?? null)}</dd>

        <dt>Vehicle</dt>
        <dd>
          {dashOrText(
            ticket.vehicle?.registration ?? ticket.vehicle_reg_snapshot ?? null,
          )}
        </dd>

        <dt>Weighbridge</dt>
        <dd>{dashOrText(ticket.weighbridge?.name ?? null)}</dd>

        <dt>Yard</dt>
        <dd>{dashOrText(ticket.yard?.name ?? null)}</dd>
      </dl>
    </Card>
  );
}

function WeightsCard({ ticket }: { ticket: TicketDetailDto }): JSX.Element {
  // Net falls back to gross - tare when the server hasn't denormalised
  // net_weight_kg yet (e.g. draft tickets mid-flow).
  const netComputed =
    ticket.net_weight_kg ??
    (ticket.gross_weight_kg !== null && ticket.tare_weight_kg !== null
      ? ticket.gross_weight_kg - ticket.tare_weight_kg
      : null);

  return (
    <Card title="Weights">
      <div className={styles.weights}>
        <div className={styles.weightRow}>
          <span className={styles.weightLabel}>Gross</span>
          <span className={styles.weightValue}>
            {fmtKg(ticket.gross_weight_kg)}
          </span>
          {methodBadge(ticket.gross_method)}
        </div>
        <div className={styles.weightRow}>
          <span className={styles.weightLabel}>Tare</span>
          <span className={styles.weightValue}>
            {fmtKg(ticket.tare_weight_kg)}
          </span>
          {methodBadge(ticket.tare_method)}
        </div>
        <div className={styles.weightRow}>
          <span className={styles.weightLabel}>Net</span>
          <span className={styles.weightValue}>{fmtKg(netComputed)}</span>
        </div>
      </div>
      <div className={styles.weightMode}>
        <span>Weighing mode:</span>
        <Badge variant="default">
          {WEIGHING_MODE_LABEL[ticket.weighing_mode]}
        </Badge>
      </div>
    </Card>
  );
}

function WeightEventsCard({
  events,
}: {
  events: ReadonlyArray<WeightEventDto>;
}): JSX.Element {
  return (
    <Card>
      <details className={styles.eventsDetails}>
        <summary>Weight events ({events.length})</summary>
        {events.length === 0 ? (
          <p className={styles.muted}>No weight events recorded.</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th>Type</th>
                <th className={styles.numCol}>Weight</th>
                <th>Method</th>
                <th>Bridge</th>
                <th>Stable?</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id}>
                  <td>{e.attempt_no}</td>
                  <td>
                    {e.weight_type.charAt(0).toUpperCase() + e.weight_type.slice(1)}
                  </td>
                  <td className={styles.numCol}>{fmtKg(e.weight_kg)}</td>
                  <td>{METHOD_LABEL[e.method]}</td>
                  <td>{e.weighbridge?.name ?? '—'}</td>
                  <td>{e.is_stable ? 'Yes' : 'No'}</td>
                  <td>{fmtDateTime(e.captured_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </details>
    </Card>
  );
}

function LineItemsCard({ ticket }: { ticket: TicketDetailDto }): JSX.Element {
  return (
    <Card title="Line items" flush>
      <table className={styles.table} data-testid="lines-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Product</th>
            <th>Mode</th>
            <th className={styles.numCol}>Gross</th>
            <th className={styles.numCol}>Tare</th>
            <th className={styles.numCol}>Net / Qty</th>
            <th className={styles.numCol}>Unit price</th>
            <th>Basis</th>
            <th className={styles.numCol}>VAT %</th>
            <th className={styles.numCol}>VAT £</th>
            <th className={styles.numCol}>Total</th>
          </tr>
        </thead>
        <tbody>
          {ticket.lines.length === 0 ? (
            <tr>
              <td colSpan={11}>
                <span className={styles.muted}>No lines on this ticket.</span>
              </td>
            </tr>
          ) : (
            ticket.lines.map((line) => (
              <tr key={line.id}>
                <td>{line.line_no}</td>
                <td>
                  {line.product?.name ??
                    line.product_name_snapshot ??
                    '—'}
                </td>
                <td>{LINE_MODE_LABEL[line.mode]}</td>
                <td className={styles.numCol}>
                  {line.mode === 'weight' ? fmtKg(line.gross_weight_kg) : '—'}
                </td>
                <td className={styles.numCol}>
                  {line.mode === 'weight' ? fmtKg(line.tare_weight_kg) : '—'}
                </td>
                <td className={styles.numCol}>
                  {line.mode === 'weight'
                    ? fmtKg(line.net_weight_kg)
                    : fmtNumber(line.quantity)}
                </td>
                <td className={styles.numCol}>{fmtCurrency(line.unit_price)}</td>
                <td>{basisBadge(line.unit_price_basis)}</td>
                <td className={styles.numCol}>
                  {line.vat_rate_snapshot_pct === null
                    ? '—'
                    : `${line.vat_rate_snapshot_pct.toFixed(1)}%`}
                </td>
                <td className={styles.numCol}>{fmtCurrency(line.vat_amount)}</td>
                <td className={styles.numCol}>{fmtCurrency(line.line_total)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <div className={styles.totalsStrip}>
        <span className={styles.totalCell}>
          <span className={styles.totalLabel}>Subtotal</span>
          <span className={styles.totalValue}>{fmtCurrency(ticket.subtotal)}</span>
        </span>
        <span className={styles.totalCell}>
          <span className={styles.totalLabel}>VAT total</span>
          <span className={styles.totalValue}>{fmtCurrency(ticket.vat_total)}</span>
        </span>
        <span className={styles.totalCell}>
          <span className={styles.totalLabel}>Total</span>
          <span
            className={`${styles.totalValue} ${styles.totalValueGrand}`}
          >
            {fmtCurrency(ticket.total)}
          </span>
        </span>
      </div>
    </Card>
  );
}

function NotesCard({ ticket }: { ticket: TicketDetailDto }): JSX.Element {
  return (
    <Card title="Notes">
      <div className={styles.notesBlock}>
        {ticket.notes !== null ? (
          <div>
            <p className={styles.notesLabel}>Notes</p>
            <p className={styles.notesText}>{ticket.notes}</p>
          </div>
        ) : null}
        {ticket.internal_notes !== null ? (
          <div>
            <p className={styles.notesLabel}>Internal notes</p>
            <p className={styles.notesText}>{ticket.internal_notes}</p>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

interface ActionsCardProps {
  ticket: TicketDetailDto;
  /** True when another staff member holds a fresh lock — edit actions disabled. */
  lockedByAnother: boolean;
  /** Holder's name for the disabled-button tooltip (null when not locked-out). */
  lockedByName: string | null;
  onContinue: () => void;
  onSecondWeigh: () => void;
  onReissue: () => void;
  isReissuing: boolean;
  onMarkOpen: () => void;
  isMarkingOpen: boolean;
  onOpenVoidModal: () => void;
  onSendAdviceNote: () => void;
  isSendingAdviceNote: boolean;
}

function ActionsCard({
  ticket,
  lockedByAnother,
  lockedByName,
  onContinue,
  onSecondWeigh,
  onReissue,
  isReissuing,
  onMarkOpen,
  isMarkingOpen,
  onOpenVoidModal,
  onSendAdviceNote,
  isSendingAdviceNote,
}: ActionsCardProps): JSX.Element {
  // Statuses that can still mutate forward (continue editing, void).
  const inFlight = ticket.status === 'draft' || ticket.status === 'open';

  // Two-pass shortcut: a ticket that has a gross but no tare still needs its
  // second (tare) weigh. Replaces the old `awaiting_second_weigh` status gate.
  const needsSecondWeigh =
    inFlight &&
    ticket.gross_weight_kg !== null &&
    ticket.tare_weight_kg === null;

  const showWtn = hasWasteLine(ticket);
  const showHazardous = hasHazardousLine(ticket);

  return (
    <Card title="Actions">
      {ticket.status === 'voided' && ticket.void_reason ? (
        <div className={styles.voidBanner}>
          Voided: {ticket.void_reason}
        </div>
      ) : null}

      <div className={styles.actionsCard}>
        {/* In-flight tickets are previewed read-only here. "Continue ticket"
            opens the editor (WbNewTicketPage), which acquires the advisory
            lock (migration 167) for the editing session. */}
        {inFlight ? (
          <>
            <Button
              variant="primary"
              onClick={onContinue}
              disabled={lockedByAnother}
              title={
                lockedByAnother ? `Locked by ${lockedByName}` : undefined
              }
              data-testid="continue-ticket-button"
            >
              Continue ticket
            </Button>
            {needsSecondWeigh ? (
              <Button
                variant="secondary"
                onClick={onSecondWeigh}
                disabled={lockedByAnother}
                title={
                  lockedByAnother ? `Locked by ${lockedByName}` : undefined
                }
                data-testid="second-weigh-button"
              >
                Capture second weigh
              </Button>
            ) : null}
            {ticket.status === 'draft' ? (
              <Button
                variant="secondary"
                onClick={onMarkOpen}
                disabled={isMarkingOpen}
                data-testid="mark-open-button"
              >
                Mark as Open
              </Button>
            ) : null}
            {/*
              No void button here: only `completed` tickets can be voided
              (the server returns 422 otherwise). The void affordance lives
              in the completed block below.
            */}
          </>
        ) : null}

        {ticket.status === 'completed' ? (
          <>
            <WbPrintButton
              label="Print ticket"
              templateType="wb_ticket"
              ticketEncId={ticket.enc_id}
            />
            <WbPrintButton
              label="Print advice note"
              templateType="wb_advice_note"
              ticketEncId={ticket.enc_id}
            />
            {showWtn ? (
              <WbPrintButton
                label="Waste Transfer Note"
                templateType="wb_wtn"
                ticketEncId={ticket.enc_id}
              />
            ) : null}
            {showHazardous ? (
              <WbPrintButton
                label="Hazardous consignment note"
                templateType="wb_hazardous_consignment"
                ticketEncId={ticket.enc_id}
              />
            ) : null}
            <Button
              variant="secondary"
              icon={Send}
              onClick={onSendAdviceNote}
              loading={isSendingAdviceNote}
              title="Re-queue the advice-note email to the customer."
              data-testid="send-advice-note-button"
            >
              Send advice note
            </Button>
            <Button
              variant="danger"
              icon={Ban}
              onClick={onOpenVoidModal}
              data-testid="void-ticket-button"
            >
              Void ticket
            </Button>
            <Button
              variant="secondary"
              icon={RotateCcw}
              disabled
              title="Coming in C11"
            >
              Reissue
            </Button>
          </>
        ) : null}

        {ticket.status === 'voided' ? (
          <Button
            variant="primary"
            icon={RotateCcw}
            onClick={onReissue}
            loading={isReissuing}
            data-testid="reissue-button"
          >
            Reissue ticket
          </Button>
        ) : null}
      </div>
    </Card>
  );
}

// -- ActivityCard ---------------------------------------------------------
//
// Lazy-loads the per-ticket activity feed from wb_activity (mig 142) via
// GET /wb/tickets/:enc_id/activity. The server resolves the enc_id to a numeric
// PK internally (tenant-scoped, 404 for unknown/cross-tenant) — raw numeric ids
// never cross the HTTP boundary.
interface ActivityCardProps {
  ticketEncId: string;
}

function MetadataList({
  metadata,
}: {
  metadata: Record<string, unknown>;
}): JSX.Element {
  const entries = Object.entries(metadata);
  if (entries.length === 0) return <span className={styles.muted}>—</span>;
  return (
    <dl className={styles.metaList}>
      {entries.map(([k, v]) => (
        <div key={k} className={styles.metaRow}>
          <dt className={styles.metaKey}>{k.replace(/_/g, ' ')}</dt>
          <dd className={styles.metaVal}>{v === null ? '—' : String(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

function ActivityCard({ ticketEncId }: ActivityCardProps): JSX.Element {
  const [enabled, setEnabled] = useState(false);

  const activityQ = useQuery<ActivityLogResponse>({
    queryKey: ['wb', 'ticket', ticketEncId, 'activity'],
    queryFn: async () => {
      const params = new URLSearchParams({ page: '1', limit: '50' });
      const res = await apiClient.get<ActivityLogResponse>(
        `${endpoints.wb.ticketActivity(ticketEncId)}?${params.toString()}`,
      );
      return res.data;
    },
    enabled,
    staleTime: 30_000,
    retry: false,
  });

  return (
    <Card>
      <details
        className={styles.eventsDetails}
        onToggle={(e) => {
          if ((e.currentTarget as HTMLDetailsElement).open) setEnabled(true);
        }}
        data-testid="activity-details"
      >
        <summary>Ticket activity</summary>
        {!enabled ? (
          <p className={styles.muted}>Open this section to load activity.</p>
        ) : activityQ.isLoading ? (
          <div className={styles.loading}>
            <Spinner label="Loading activity" />
          </div>
        ) : activityQ.isError ? (
          <p className={styles.muted}>Activity log not available.</p>
        ) : (activityQ.data?.activity ?? []).length === 0 ? (
          <p className={styles.muted}>No activity recorded yet.</p>
        ) : (
          <table className={styles.table} data-testid="activity-table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Details</th>
                <th>Actor</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {(activityQ.data?.activity ?? []).map((entry) => {
                const ev = eventLabel(entry.event_type);
                return (
                  <tr key={entry.id}>
                    <td>
                      <Badge variant={ev.variant}>{ev.label}</Badge>
                    </td>
                    <td>
                      <MetadataList metadata={entry.metadata} />
                    </td>
                    <td>{entry.actor_name ?? 'System'}</td>
                    <td>{fmtDateTime(entry.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </details>
    </Card>
  );
}

function PaymentCard({ ticket }: { ticket: TicketDetailDto }): JSX.Element {
  const method = ticket.payment_method
    ? ticket.payment_method.charAt(0).toUpperCase() +
      ticket.payment_method.slice(1)
    : '—';

  let balanceLine: JSX.Element;
  if (ticket.balance > 0) {
    balanceLine = (
      <p className={styles.balanceOutstanding}>
        Outstanding: {fmtCurrency(ticket.balance)}
      </p>
    );
  } else if (ticket.balance === 0) {
    balanceLine = <p className={styles.balancePaid}>Paid in full</p>;
  } else {
    balanceLine = (
      <p className={styles.balancePaid}>
        Credit: {fmtCurrency(Math.abs(ticket.balance))}
      </p>
    );
  }

  return (
    <Card title="Payment">
      <div className={styles.paymentRow}>
        <span className={styles.paymentLabel}>Method</span>
        <span className={styles.paymentValue}>{method}</span>
      </div>
      <div className={styles.paymentRow}>
        <span className={styles.paymentLabel}>Paid</span>
        <span className={styles.paymentValue}>{fmtCurrency(ticket.paid_amount)}</span>
      </div>
      <div className={styles.paymentRow}>
        <span className={styles.paymentLabel}>Balance</span>
        <span className={styles.paymentValue}>{fmtCurrency(ticket.balance)}</span>
      </div>
      {balanceLine}
      {ticket.invoice ? (
        <div className={styles.paymentRow}>
          <span className={styles.paymentLabel}>Invoice</span>
          <span className={styles.paymentValue}>
            <Link
              to={`/collections/invoices/${encodeURIComponent(ticket.invoice.enc_id)}`}
              data-testid="payment-invoice-link"
            >
              {ticket.invoice.invoice_number}
            </Link>
          </span>
        </div>
      ) : null}
    </Card>
  );
}

function VoidInfoCard({ ticket }: { ticket: TicketDetailDto }): JSX.Element {
  const invoice = ticket.invoice ?? null;
  const cancelled = invoice !== null && invoice.status === 'cancelled';
  return (
    <Card title="Void info">
      <dl className={styles.dl}>
        <dt>Voided at</dt>
        <dd>{fmtDateTime(ticket.voided_at)}</dd>
        <dt>Void reason</dt>
        <dd>{dashOrText(ticket.void_reason)}</dd>
        {invoice ? (
          <>
            <dt>{cancelled ? 'Credit raised' : 'Invoice'}</dt>
            <dd>
              <Link
                to={`/collections/invoices/${encodeURIComponent(invoice.enc_id)}`}
                data-testid="void-credit-note-link"
              >
                {invoice.invoice_number}
                {cancelled ? ' (cancelled)' : ''} — View invoice
              </Link>
            </dd>
          </>
        ) : null}
      </dl>
    </Card>
  );
}
